/** Guided Decision proposal (merchant view): problem → savings → costs → plan → equipment → commitments → apply. */
import React, { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;
const money = (n, d = 0) => `${n < 0 ? '-' : ''}$${Math.abs(n || 0).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d })}`;

function Section({ n, title, children, testid }) {
  return (
    <section className="border-t border-slate-200 py-8" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{String(n).padStart(2, '0')}</p>
      <h2 className="mt-1 text-lg font-semibold text-slate-900">{title}</h2>
      <div className="mt-4 text-[15px] leading-relaxed text-slate-700">{children}</div>
    </section>
  );
}

function Row({ label, value, strong, testid }) {
  return <div className={`flex justify-between border-b border-slate-100 py-2 ${strong ? 'font-semibold text-slate-900' : ''}`}><span>{label}</span><span data-testid={testid}>{value}</span></div>;
}

function Alternatives({ options, rec }) {
  const [open, setOpen] = useState(false);
  const alts = Object.entries(options).filter(([k]) => k !== rec);
  if (!alts.length) return null;
  return (
    <div className="mt-4">
      <button type="button" className="text-sm text-slate-500 underline" onClick={() => setOpen(!open)} data-testid="guided-alternatives-toggle">{open ? 'Hide' : 'Compare'} {alts.length} other option{alts.length > 1 ? 's' : ''}</button>
      {open && alts.map(([k, o]) => (
        <div key={k} className="mt-3 rounded-lg border border-slate-200 p-4 text-sm" data-testid={`guided-alt-${k}`}>
          <p className="font-medium text-slate-900">{o.label}</p><p className="text-slate-500">{o.rate}</p>
          <p className="mt-1">Total {money(o.proposed_total, 2)}/mo · {o.monthly_savings >= 0 ? `saves ${money(o.monthly_savings)}/mo` : `costs ${money(-o.monthly_savings)}/mo more`}</p>
        </div>))}
    </div>
  );
}

export default function GuidedDecisionView({ p, accepted, onAccept }) {
  const [noLink, setNoLink] = useState(false);
  const go = async () => { const r = await onAccept(); if (r && !r.application_url) setNoLink(true); };
  const e = p.engine;
  const q = e.quote;
  const rec = q.options[e.recommended] || Object.values(q.options)[0];
  const eq = q.equipment;
  return (
    <div className="min-h-screen bg-slate-50" data-testid="guided-proposal">
      {p.cover_image_url && <img src={`${API}${p.cover_image_url}`} alt="" className="max-h-[320px] w-full object-cover" data-testid="proposal-cover-image" />}
      <main className="mx-auto max-w-2xl bg-white px-6 pb-20 pt-10 shadow-sm sm:px-10">
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">Prepared for {p.business_name}</p>
        <Section n={1} title="Where you are today" testid="guided-problem"><p data-testid="guided-opening">{e.opening}</p>
          {e.discovery_quotes?.map((x) => <blockquote key={x} className="mt-3 border-l-4 border-slate-300 pl-3 italic text-slate-600">“{x}”</blockquote>)}</Section>
        {rec.annual_savings > 0 && <Section n={2} title="Your estimated savings" testid="guided-savings">
          <p className="text-4xl font-bold text-emerald-600 sm:text-5xl" data-testid="guided-annual-savings">{money(rec.annual_savings)}<span className="ml-2 text-base font-medium text-slate-500">a year</span></p>
          <p className="mt-1 text-slate-500" data-testid="guided-monthly-savings">{money(rec.monthly_savings)} a month · first year net {money(rec.first_year_net)} after upfront costs</p>
        </Section>}
        <Section n={3} title="Current versus proposed cost (monthly)" testid="guided-costs">
          <Row label="What you pay today" value={money(q.current_monthly_cost, 2)} testid="guided-current" />
          <Row label="Proposed processing cost" value={money(rec.processing, 2)} />
          <Row label="Software and other recurring fees" value={money(rec.software_and_fees, 2)} />
          <Row label="Estimated net monthly savings" value={money(rec.monthly_savings, 2)} strong testid="guided-net" />
        </Section>
        <Section n={4} title={`Recommended plan: ${rec.label}`} testid="guided-plan"><p data-testid="guided-rate">{rec.rate}</p><Alternatives options={q.options} rec={e.recommended} /></Section>
        {eq && <Section n={5} title="Equipment" testid="guided-equipment">
          {eq.logo_url && <img src={eq.logo_url} alt={eq.name} className="mb-3 h-10" />}
          <p className="font-medium text-slate-900">{eq.name}</p>
          {eq.free ? (<ul className="mt-2 list-disc space-y-1 pl-5 text-sm" data-testid="guided-equipment-terms">
            <li>No upfront cost while monthly volume stays above {money(eq.terms.min_monthly_volume)}.</li>
            <li>Owned by {eq.terms.owner}; {eq.terms.term_months}-month term.</li>
            <li>Cancelling early costs {money(eq.terms.cancellation_fee)}; return the equipment within {eq.terms.return_days} days.</li>
            <li>{eq.terms.below_threshold}</li></ul>) : <p className="text-sm">One-time cost {money(eq.upfront_due, 2)}.</p>}
        </Section>}
        <Section n={eq ? 6 : 5} title="Installation and training" testid="guided-install"><p>Your rep sets up the equipment, walks your team through it and stays your point of contact after launch.</p></Section>
        <Section n={eq ? 7 : 6} title="Commitments" testid="guided-commitments">
          <p>{eq?.free ? `${eq.terms.term_months}-month term tied to the free equipment.` : 'No long-term contract required for processing.'} Pricing shown is an estimate from your statement; final pricing is confirmed in your application.</p>
          <p className="mt-3 text-slate-500" data-testid="guided-consequence">{e.consequence}</p>
          {e.deadline && <p className="mt-2 font-medium text-slate-900" data-testid="guided-deadline">This offer is available until {new Date(`${e.deadline}T12:00:00`).toLocaleDateString()}.</p>}
        </Section>
        <section className="border-t border-slate-200 pt-8 text-center">
          {accepted && <p className="mb-4 text-base font-semibold text-emerald-600" data-testid="guided-accepted">{noLink ? 'Thank you! Your rep has been notified and will send your application today.' : 'Thank you — opening your application…'}</p>}
          <button type="button" onClick={go} className="w-full animate-bounce rounded-full bg-emerald-600 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-emerald-600/30 transition-colors hover:animate-none hover:bg-emerald-700 sm:w-auto" data-testid="guided-start-application">{accepted ? 'Continue my application' : 'Start my application'}</button>
          <p className="mt-3 text-xs text-slate-400">Next: a short application (about 10 minutes) → approval → equipment shipped → you're live.</p>
        </section>
      </main>
    </div>
  );
}
